import React, { useEffect, useMemo, useRef, useState } from 'react';
import ModalPortal from './ModalPortal';
import '../styles/activitylog.css';
import {
  buildActivityLogQueryParams,
  getActivityLogActivity,
  getActivityLogGrade,
  normalizeActivityLogPayload,
  shouldShowActivityLogFilters,
} from './activityLog.utils';
import { buildScopedApiUrl } from './analyticsEndpoints';
import { buildAuthHeaders } from './session.utils';
import { sortStudentsByName } from './studentProgress.utils';
import { TablePrintButton } from './TablePrintButton';
import { PrintableTableReport } from './PrintableTableReport';
import { collectAuthorizedReportRows, formatReportContext } from './tableReporting.utils';
import { usePreparedReportPrint } from './usePreparedReportPrint';

export default function ActivityLog({ limit = 50, role = 'admin', userId = null, allowActivityReset = false }) {
  const [activities, setActivities] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, pages: 1, current_page: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [parentChildren, setParentChildren] = useState([]);
  const [childrenLoaded, setChildrenLoaded] = useState(role !== 'parent');
  const [selectedChildId, setSelectedChildId] = useState('');
  const [reportError, setReportError] = useState('');
  const [activityRevision, setActivityRevision] = useState(0);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetConfirmation, setResetConfirmation] = useState('');
  const [resettingActivity, setResettingActivity] = useState(false);
  const [resetActivityError, setResetActivityError] = useState('');
  const [resetConfirmationTouched, setResetConfirmationTouched] = useState(false);
  const activityRequestRevision = useRef(0);
  const { preparedRows, hasPreparedReport, preparing: reportPreparing, prepareAndPrint } = usePreparedReportPrint();
  const requiresScopedUser = role === 'teacher' || role === 'parent';
  const scopedUserReady = !requiresScopedUser || Boolean(userId);
  const showFilters = shouldShowActivityLogFilters(role);
  const isParentView = role === 'parent';
  const canResetActivityLog = role === 'admin' && allowActivityReset;

  useEffect(() => {
    if (!showFilters) {
      setDebouncedSearch('');
      setSearchTerm('');
      setCurrentPage(1);
      return undefined;
    }

    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim().toLowerCase());
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, showFilters]);

  useEffect(() => {
    let cancelled = false;

    const loadParentChildren = async () => {
      if (!isParentView || !userId) {
        setChildrenLoaded(true);
        return;
      }

      setChildrenLoaded(false);
      try {
        const response = await fetch(buildScopedApiUrl('/api/parent/children', 'parent'), {
          headers: buildAuthHeaders(),
        });
        if (!response.ok) throw new Error('Failed to load children');
        const payload = await response.json();
        const children = sortStudentsByName(Array.isArray(payload?.children) ? payload.children : []);
        if (cancelled) return;
        setParentChildren(children);
        // Parent Activity Log defaults to all linked children. A specific child
        // can still be selected from the filter when the parent wants a
        // child-only view. Never infer a child by position.
        setSelectedChildId((current) => (
          current && children.some((child) => String(child.student_id || child.id) === String(current))
            ? current
            : ''
        ));
      } catch (err) {
        console.error('Error fetching parent children:', err);
        if (!cancelled) {
          setParentChildren([]);
          setSelectedChildId('');
        }
      } finally {
        if (!cancelled) setChildrenLoaded(true);
      }
    };

    loadParentChildren();
    return () => {
      cancelled = true;
    };
  }, [isParentView, userId]);

  useEffect(() => {
    const requestRevision = activityRequestRevision.current + 1;
    activityRequestRevision.current = requestRevision;
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    let cancelled = false;
    const fetchActivityLogs = async () => {
      if (!scopedUserReady || !childrenLoaded) {
        return;
      }

      // Empty child selection means "all children belonging to this parent".
      // The backend parent scope remains authoritative and prevents unrelated
      // students from being returned.
      setLoading(true);
      setError(null);
      try {
        const queryParams = buildActivityLogQueryParams({
          limit,
          itemsPerPage,
          currentPage,
          role,
          userId,
          selectedStudentId: isParentView ? selectedChildId : '',
          debouncedSearch,
        });

        const response = await fetch(buildScopedApiUrl(`/api/activity-logs?${queryParams.toString()}`, role), {
          headers: buildAuthHeaders(),
          ...(controller ? { signal: controller.signal } : {}),
        });
        if (!response.ok) throw new Error('Failed to load activity logs');

        const payload = await response.json();
        if (cancelled || requestRevision !== activityRequestRevision.current) return;
        const normalized = normalizeActivityLogPayload(payload);
        setActivities(normalized.records);
        setPagination(normalized.pagination);
      } catch (err) {
        if (cancelled || controller?.signal.aborted || requestRevision !== activityRequestRevision.current) return;
        console.error('Error fetching activity logs:', err);
        setActivities([]);
        setPagination({ total: 0, pages: 1, current_page: 1 });
        setError('Unable to load activity logs right now.');
      } finally {
        if (!cancelled && requestRevision === activityRequestRevision.current) setLoading(false);
      }
    };

    fetchActivityLogs();
    return () => {
      cancelled = true;
      controller?.abort();
    };
  }, [activityRevision, childrenLoaded, currentPage, debouncedSearch, isParentView, limit, role, scopedUserReady, selectedChildId, userId]);

  const closeResetDialog = () => {
    if (resettingActivity) return;
    setResetDialogOpen(false);
    setResetConfirmation('');
    setResetConfirmationTouched(false);
    setResetActivityError('');
  };

  const validateResetConfirmation = (value) => {
    const confirmation = String(value || '');
    if (!confirmation.trim()) return 'Please fill out this field.';
    if (confirmation !== 'RESET') return 'Type RESET to confirm this action.';
    return '';
  };

  const submitActivityReset = async (event) => {
    event.preventDefault();
    setResetConfirmationTouched(true);
    const validationError = validateResetConfirmation(resetConfirmation);
    if (validationError) {
      setResetActivityError(validationError);
      return;
    }

    setResettingActivity(true);
    setResetActivityError('');
    try {
      const response = await fetch('/api/activity-logs/reset', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...buildAuthHeaders(),
        },
        body: JSON.stringify({ confirmation: resetConfirmation }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || 'Unable to reset Student quest activity.');

      setResetDialogOpen(false);
      setResetConfirmation('');
      setCurrentPage(1);
      setActivityRevision((revision) => revision + 1);
    } catch (err) {
      setResetActivityError(err.message || 'Unable to reset Student quest activity.');
    } finally {
      setResettingActivity(false);
    }
  };

  const totalPages = Math.max(1, pagination.pages || 1);
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);
  const latestActivity = activities[0] || null;
  const reportRows = hasPreparedReport ? preparedRows : activities;
  const reportScope = debouncedSearch
    ? `Search: ${debouncedSearch}`
    : (isParentView ? 'Selected child' : 'All authorised activity records');
  const reportLabel = isParentView || /^(?:\d{6}|\d{8}|\d{2}-\d{6})$/.test(debouncedSearch)
    ? 'Print Student Activity'
    : 'Print Filtered Activity Log';
  const reportColumns = [
    {
      header: 'Date / Time',
      value: (row) => {
        const timestamp = row.activity_timestamp || row.created_at || row.last_played;
        return timestamp ? `${formatDate(timestamp)} ${formatTime(timestamp)}` : null;
      },
    },
    { header: 'Student ID', value: (row) => row.game_student_id },
    { header: 'Student Name', value: (row) => row.student_name },
    { header: 'Grade', value: (row) => getActivityLogGrade(row) },
    { header: 'Section', value: (row) => row.section },
    { header: 'Activity', value: (row) => getActivityLogActivity(row) },
  ];

  const prepareActivityReport = async () => {
    setReportError('');
    await prepareAndPrint(async () => {
      try {
        return await collectAuthorizedReportRows({
          pageSize: 200,
          loadPage: async ({ page, limit: reportLimit }) => {
            const queryParams = buildActivityLogQueryParams({
              limit: reportLimit,
              itemsPerPage: reportLimit,
              currentPage: page,
              role,
              userId,
              selectedStudentId: isParentView ? selectedChildId : '',
              debouncedSearch,
            });
            const response = await fetch(buildScopedApiUrl(`/api/activity-logs?${queryParams.toString()}`, role), {
              headers: buildAuthHeaders(),
            });
            if (!response.ok) throw new Error('Unable to prepare the activity report');
            const payload = await response.json();
            const normalized = normalizeActivityLogPayload(payload);
            return { rows: normalized.records, pagination: normalized.pagination };
          },
        });
      } catch (err) {
        setReportError('Unable to prepare the activity report right now.');
        throw err;
      }
    });
  };

  const summaryCards = useMemo(() => ([
    { label: 'Total records', value: pagination.total || activities.length || 0 },
    { label: 'Current page', value: `${currentPage} / ${totalPages}` },
    {
      label: 'Latest activity',
      value: latestActivity ? formatDate(latestActivity.activity_timestamp || latestActivity.created_at || latestActivity.last_played) : '-'
    },
  ]), [activities.length, currentPage, latestActivity, pagination.total, totalPages]);

  if (loading && activities.length === 0 && !debouncedSearch) {
    return <div className="al-loading">Loading activity log...</div>;
  }

  if (!scopedUserReady || !childrenLoaded) {
    return <div className="al-loading">Loading activity log...</div>;
  }

  if (error) {
    return <div className="al-error">{error}</div>;
  }

  return (
    <div className="activity-log-container">
      <div className="al-header">
        <h2>{role === 'parent' ? 'Child Activity Log' : 'Student Activity Log'}</h2>
        <p className="al-subtitle">
          {role === 'parent'
            ? "Review gameplay activity, timestamps, and learning progress in a clean parent-focused layout."
            : 'Monitor gameplay sessions, activity details, and engagement metrics with aligned records.'}
        </p>
      </div>

      <div className="al-summary-bar">
        {summaryCards.map((card) => (
          <div key={card.label} className="al-summary-card">
            <span>{card.label}</span>
            <strong>{card.value}</strong>
          </div>
        ))}
      </div>

      {showFilters && (
        <div className="al-filters">
          <div className="filter-group">
            <label htmlFor="search-input">Search Student</label>
            <input
              id="search-input"
              type="text"
            placeholder="Search name, Student ID, grade, time, activity, or difficulty..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="filter-input search-input"
            />
          </div>

          <button
            type="button"
            onClick={() => {
              setSearchTerm('');
              setCurrentPage(1);
            }}
            className="btn-reset"
          >
            Clear Filters
          </button>
        </div>
      )}

      {isParentView && parentChildren.length > 0 && (
        <div className="al-filters parent-child-filter">
          <div className="filter-group">
            <label htmlFor="child-filter">Child</label>
            <select
              id="child-filter"
              value={selectedChildId}
              onChange={(event) => {
                setSelectedChildId(event.target.value);
                setCurrentPage(1);
              }}
              className="filter-input"
            >
              <option value="">All Children</option>
              {parentChildren.map((child) => {
                const childId = String(child.student_id || child.id);
                return (
                  <option key={childId} value={childId}>
                    {child.student_name || child.name || `Child ${childId}`}
                  </option>
                );
              })}
            </select>
          </div>
        </div>
      )}

      <div className="al-results-info">
        <div className="table-report-controls">
          <TablePrintButton
            reportTitle={role === 'parent' ? 'Child Activity Log' : 'Activity Log'}
            reportContext={formatReportContext({ scope: reportScope, recordCount: pagination.total || activities.length })}
            label={reportLabel}
            showPrintHeading={false}
            preparing={reportPreparing}
            onPrint={prepareActivityReport}
          />
          {canResetActivityLog && (
            <button
              type="button"
              className="btn-reset activity-log-reset-button"
              onClick={() => {
                setResetActivityError('');
                setResetConfirmationTouched(false);
                setResetDialogOpen(true);
              }}
            >
              Reset Activity Log
            </button>
          )}
        </div>
        <span className="results-count">
          Showing {activities.length === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1} - {Math.min((currentPage - 1) * itemsPerPage + activities.length, pagination.total || activities.length)} of {pagination.total || activities.length} records
        </span>
      </div>
      {reportError && <p className="fallback-note validation-error-text no-print">{reportError}</p>}

      {activities.length === 0 ? (
        <div className="al-empty">
          <p>{showFilters ? 'No activity records found. Try adjusting your filters.' : 'No activity records found yet.'}</p>
        </div>
      ) : (
        <>
          <div className="al-table-wrapper">
            <table className="al-table">
              <thead>
                <tr>
                  <th>Student Name</th>
                  <th>Student ID</th>
                  <th>Grade</th>
                  <th>Time</th>
                  <th>Activity</th>
                </tr>
              </thead>
              <tbody>
                {activities.map((activity, index) => (
                  <tr key={activity.id ?? `${activity.student_id}-${activity.activity_timestamp}-${index}`} className={index % 2 === 0 ? 'even' : 'odd'}>
                    <td className="name-cell">
                      <strong>{activity.student_name || '-'}</strong>
                    </td>
                    <td>{activity.game_student_id || 'Not linked'}</td>
                    <td className="grade-cell">
                      <span className="grade-badge">{getActivityLogGrade(activity)}</span>
                    </td>
                    <td className="timestamp-cell">
                      <div className="timestamp-stack">
                        <strong>{formatTime(activity.started_at || activity.timestamp || activity.activity_timestamp || activity.last_played || activity.created_at)}</strong>
                      </div>
                    </td>
                    <td className="quest-cell">
                      <span className="quest-name">{getActivityLogActivity(activity)}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

        </>
      )}
      <div className="al-pagination no-print" aria-label="Activity Log pagination">
        <span className="pagination-range">
          {activities.length === 0
            ? '0 records'
            : `Showing ${(currentPage - 1) * itemsPerPage + 1} - ${Math.min((currentPage - 1) * itemsPerPage + activities.length, pagination.total || activities.length)} of ${pagination.total || activities.length} records`}
        </span>
        <button
          type="button"
          onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
          disabled={currentPage === 1}
          className="pagination-btn"
        >
          Previous
        </button>
        <span className="pagination-info">
          Page {currentPage} of {totalPages}
        </span>
        <button
          type="button"
          onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
          disabled={currentPage === totalPages}
          className="pagination-btn"
        >
          Next
        </button>
      </div>
      <PrintableTableReport
        title={reportLabel.replace(/^Print /, '')}
        context={reportScope}
        rows={reportRows}
        columns={reportColumns}
      />
      {canResetActivityLog && resetDialogOpen && (
        <ModalPortal onClose={closeResetDialog}>
        <div className="activity-log-reset-backdrop" role="presentation" onMouseDown={closeResetDialog}>
          <form className="activity-log-reset-dialog" role="dialog" aria-modal="true" aria-labelledby="activity-log-reset-title" onSubmit={submitActivityReset} onMouseDown={(event) => event.stopPropagation()}>
            <h3 id="activity-log-reset-title">Reset Activity Log</h3>
            <p>This will permanently delete all Student quest-activity records across all Students; current filters do not limit this reset.</p>
            <p>Accounts, Student progress, saves, results, playtime, questions, publications, relationships, assignments, and audit logs are not affected.</p>
            <label htmlFor="activity-log-reset-confirmation">Type RESET to confirm</label>
            <input
              id="activity-log-reset-confirmation"
              name="activity-log-reset-confirmation"
              value={resetConfirmation}
              className={resetActivityError && resetConfirmationTouched ? 'activity-log-reset-input error' : 'activity-log-reset-input'}
              onChange={(event) => {
                const value = event.target.value;
                setResetConfirmation(value);
                if (resetConfirmationTouched) {
                  setResetActivityError(validateResetConfirmation(value));
                } else {
                  setResetActivityError('');
                }
              }}
              onBlur={() => {
                setResetConfirmationTouched(true);
                setResetActivityError(validateResetConfirmation(resetConfirmation));
              }}
              disabled={resettingActivity}
              autoComplete="off"
              aria-invalid={Boolean(resetActivityError && resetConfirmationTouched)}
              aria-describedby={resetActivityError && resetConfirmationTouched ? 'activity-log-reset-confirmation-error' : undefined}
            />
            {resetActivityError && resetConfirmationTouched && (
              <p id="activity-log-reset-confirmation-error" className="activity-log-reset-error" role="alert">{resetActivityError}</p>
            )}
            <div className="activity-log-reset-actions">
              <button type="button" className="btn-reset" onClick={closeResetDialog} disabled={resettingActivity}>Cancel</button>
              <button type="submit" className="btn-reset activity-log-reset-confirm" disabled={resettingActivity || resetConfirmation !== 'RESET'}>Reset Activity Log</button>
            </div>
          </form>
        </div>
        </ModalPortal>
      )}
    </div>
  );
}

function formatTime(dateTime) {
  if (!dateTime) return '-';
  const date = new Date(dateTime);
  return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
}

function formatDate(dateString) {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
